import {
  Crown,
  Landmark,
  Briefcase,
  Building2,
  Target,
  Clock,
  Lock,
  Map,
  Compass,
  Users,
} from 'lucide-react';
import type { Service } from '../types/content';

export const services: Service[] = [
  {
    slug: 'executive-search',
    title: 'Executive Search',
    summary: 'Retained, research-led searches for leaders who shape strategy and performance.',
    description:
      'Our executive search practice pairs rigorous market research with discreet, personal outreach to reach leaders who are rarely visible through conventional channels. Every search is led by one accountable consultant from the first briefing to the final appointment.',
    benefits: [
      'Access to leaders who are not actively looking',
      'One accountable consultant throughout the search',
      'Structured assessment against an agreed success profile',
    ],
    useCases: [
      'Appointing a new divisional or business-unit leader',
      'Replacing a departing executive with minimal disruption',
      'Building leadership capability ahead of growth or change',
    ],
    process: ['Briefing and success profile', 'Market mapping and outreach', 'Assessment and shortlist', 'Interviews, offer and transition'],
    icon: Crown,
  },
  {
    slug: 'c-suite-leadership',
    title: 'C-Suite & Leadership Recruitment',
    summary: 'CEO, CFO, COO, CTO, CHRO and other enterprise-level appointments.',
    description:
      'C-suite appointments define an organisation for years. We work closely with chairs, boards and chief executives to understand the strategic context, the culture a leader will inherit, and the outcomes they will be measured against.',
    benefits: [
      'Board-level stakeholder alignment from day one',
      'Deep referencing and leadership assessment',
      'Careful management of internal and external candidates',
    ],
    useCases: ['Chief executive succession', 'Appointing a first CFO ahead of investment or listing', 'Transformation-led COO or CTO hires'],
    process: ['Stakeholder interviews', 'Leadership profile and scorecard', 'Targeted search and assessment', 'Appointment and onboarding support'],
    icon: Building2,
  },
  {
    slug: 'board-non-executive',
    title: 'Board & Non-Executive Director Search',
    summary: 'Chairs, independent directors and committee chairs who strengthen governance.',
    description:
      'We help boards build the balance of skills, independence and perspective they need. Searches begin with a review of board composition so that every appointment adds genuine capability rather than filling a seat.',
    benefits: ['Skills-matrix led role definition', 'Attention to independence and conflicts', 'Discreet approaches to sitting directors'],
    useCases: ['Appointing an audit or risk committee chair', 'Board refresh and succession', 'Adding sector or technology expertise'],
    process: ['Board composition review', 'Role specification', 'Candidate identification and due diligence', 'Nomination committee support'],
    icon: Landmark,
  },
  {
    slug: 'permanent-recruitment',
    title: 'Permanent Recruitment',
    summary: 'Considered permanent hiring for professional and management roles.',
    description:
      'For permanent roles beneath executive level we apply the same discipline: a clear brief, a defined market, careful screening and honest feedback. The result is a shortlist you can trust and a process candidates respect.',
    benefits: ['Thorough screening before introduction', 'Clear timelines and regular updates', 'Candidate care that protects your employer brand'],
    useCases: ['Growing a professional team', 'Replacing critical roles', 'Hiring into a new market or function'],
    process: ['Role briefing', 'Search and screening', 'Shortlist and interviews', 'Offer management'],
    icon: Briefcase,
  },
  {
    slug: 'senior-management',
    title: 'Senior Management Recruitment',
    summary: 'Directors and heads of function who turn strategy into delivery.',
    description:
      'Senior managers carry an organisation’s strategy into daily operation. We look for the combination of technical credibility, leadership maturity and cultural fit that makes those appointments succeed.',
    benefits: ['Competency-based evaluation', 'Market insight on remuneration and availability', 'Balanced internal and external benchmarking'],
    useCases: ['Head of function appointments', 'Regional or country leadership', 'Building a leadership bench beneath the executive team'],
    process: ['Brief and success measures', 'Targeted search', 'Structured interviews', 'Offer and transition'],
    icon: Users,
  },
  {
    slug: 'specialist-professional',
    title: 'Specialist Professional Search',
    summary: 'Hard-to-find expertise in regulated, technical and advisory disciplines.',
    description:
      'Some roles depend on rare, specific expertise. Our specialist searches are built around detailed technical briefs, peer-network research and careful verification of experience.',
    benefits: ['Technical briefs written with your subject experts', 'Reach into small, specialist talent pools', 'Verification of claimed expertise'],
    useCases: ['Regulatory, risk and compliance specialists', 'Data, privacy and cyber expertise', 'Scarce engineering and scientific skills'],
    process: ['Technical briefing', 'Peer-network research', 'Technical screening', 'Shortlist and selection'],
    icon: Target,
  },
  {
    slug: 'interim-contract',
    title: 'Interim & Contract Recruitment',
    summary: 'Experienced interim leaders and contractors for defined periods of need.',
    description:
      'When a leadership gap, project or transformation needs experienced hands quickly, we identify interim executives and contractors who can contribute from their first week.',
    benefits: ['Rapid identification of available professionals', 'Clear scope, term and deliverables', 'Option to convert to permanent where appropriate'],
    useCases: ['Covering an unexpected leadership vacancy', 'Leading a transformation or integration', 'Adding capacity for a defined project'],
    process: ['Scope and term', 'Availability search', 'Rapid assessment', 'Engagement and check-ins'],
    icon: Clock,
  },
  {
    slug: 'confidential-retained',
    title: 'Confidential & Retained Search',
    summary: 'Discreet searches where the vacancy itself is sensitive.',
    description:
      'Leadership changes are not always public. We run confidential searches under code names, with controlled information release and carefully managed candidate disclosures.',
    benefits: ['Code-named searches and controlled disclosure', 'Non-disclosure arrangements where required', 'Sensitive handling of incumbents and internal teams'],
    useCases: ['Replacing an incumbent leader discreetly', 'Pre-announcement appointments', 'Searches linked to transactions or restructuring'],
    process: ['Confidentiality framework', 'Anonymous market approach', 'Staged disclosure', 'Discreet appointment'],
    icon: Lock,
  },
  {
    slug: 'talent-mapping',
    title: 'Talent Mapping & Market Intelligence',
    summary: 'A clear view of who leads in your market, and where.',
    description:
      'Talent mapping gives you an informed picture of leadership talent across competitors and adjacent sectors — useful for succession planning, organisational design and future hiring, even before a vacancy exists.',
    benefits: ['Named or anonymised market maps', 'Insight into structures and remuneration trends', 'Foundations for succession planning'],
    useCases: ['Succession planning', 'Entering a new market', 'Benchmarking an existing leadership team'],
    process: ['Scope and target organisations', 'Research and validation', 'Analysis and insight', 'Presentation and recommendations'],
    icon: Map,
  },
  {
    slug: 'talent-advisory',
    title: 'Strategic Talent Advisory',
    summary: 'Counsel on leadership structure, succession and hiring strategy.',
    description:
      'Beyond individual searches, we advise leadership teams on how to structure, attract and retain the talent their strategy requires — from succession frameworks to hiring-process design.',
    benefits: ['Independent perspective on leadership structure', 'Practical succession frameworks', 'Improved hiring processes and candidate experience'],
    useCases: ['Preparing for a period of growth', 'Designing a succession plan', 'Reviewing an underperforming hiring process'],
    process: ['Diagnostic conversations', 'Review and analysis', 'Recommendations', 'Implementation support'],
    icon: Compass,
  },
];
